/**
 * IconButton — uniform 32×32 icon button used by the chat input toolbar.
 *
 * Interior icons are 18×18 (`h-[18px] w-[18px]`) so they read clearly in the
 * 32px button without leaving it looking empty. The component handles
 * hover / active / disabled visual states using semantic CSS tokens so
 * dark mode is automatic.
 */

import type { ReactNode } from 'react';
import { cn } from '@/utils/classNames';

export interface IconButtonProps {
  icon: ReactNode;
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  className?: string;
}

export function IconButton({
  icon,
  label,
  active,
  disabled,
  onClick,
  className,
}: IconButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={active}
      title={label}
      className={cn(
        'inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
        'transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-stellar-primary',
        'disabled:cursor-not-allowed disabled:opacity-50',
        active
          ? cn(
              'bg-bg-tertiary text-stellar-primary hover:bg-bg-secondary',
              'dark:bg-stellar-primary/30 dark:hover:bg-stellar-primary/40'
            )
          : 'text-text-secondary hover:bg-bg-secondary',
        className
      )}
    >
      {icon}
    </button>
  );
}
